/**
 * Client wrappers for /api/me/range and /api/me/mode.
 *
 * The API contract uses `mode: "actual"` (singular) per the spec; the SPA
 * shell uses `"actuals"` (plural) historically (designv2 baseline). We
 * translate at the boundary so the rest of the SPA does not need to care.
 */
import { apiFetch } from "./api";

export type ApiMode = "planned" | "actual";
export type SpaMode = "planned" | "actuals";

export interface MonthRange {
  rangeStart: string;
  rangeEnd: string;
  mode: SpaMode;
}

interface ApiMonthRange {
  rangeStart: string;
  rangeEnd: string;
  mode: ApiMode;
}

function toSpa(api: ApiMonthRange): MonthRange {
  return {
    rangeStart: api.rangeStart,
    rangeEnd: api.rangeEnd,
    mode: api.mode === "actual" ? "actuals" : "planned",
  };
}

function toApi(mode: SpaMode): ApiMode {
  return mode === "actuals" ? "actual" : "planned";
}

export async function getMyRange(): Promise<MonthRange | null> {
  const res = await apiFetch("/api/me/range");
  if (!res.ok) throw new Error(`getMyRange: ${res.status}`);
  const body = (await res.json()) as Partial<ApiMonthRange>;
  if (!body.rangeStart || !body.rangeEnd || !body.mode) return null;
  return toSpa(body as ApiMonthRange);
}

export async function patchMyRange(args: {
  rangeStart?: string;
  rangeEnd?: string;
}): Promise<MonthRange> {
  const res = await apiFetch("/api/me/range", {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(args),
  });
  if (!res.ok) throw new Error(`patchMyRange: ${res.status}`);
  return toSpa((await res.json()) as ApiMonthRange);
}

export async function patchMyMode(mode: SpaMode): Promise<MonthRange> {
  const res = await apiFetch("/api/me/mode", {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ mode: toApi(mode) }),
  });
  if (!res.ok) throw new Error(`patchMyMode: ${res.status}`);
  return toSpa((await res.json()) as ApiMonthRange);
}
